import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, session } from '../../shell/bridge';
import { Composer } from '../../components/Composer';
import { RevealHistory } from '../RevealHistory';
/* Founder email 2026-10-27: the inline past-reveals list shows the same bounded
   card as the full reveal history (one component, not a lookalike). */
import { RevealCard } from '../../components/RevealCard';
/* Jonathan 2026-10-30 (card ZXJan7hSZq2G): the confetti belongs to the moment
   BOTH answers are in, so the reveal hero — not the kudos toast — is where it
   is earned. */
import { SkeletonBlock } from '../../ui/Skeleton';
import { logTodayAction } from '../../lib/todayMetrics';
import './questions.css';

interface QuestionDay {
  questionId: string;
  dateKey: string;
  prompt: string;
  state: 'pending' | 'revealed';
  deadlineAt: number;
  revealedAt: number | null;
  answeredBy: string[];
  /** Engine metadata (epic tC4MP1JArxmm). Absent on pre-engine payloads. */
  theme?: string;
  format?: 'open' | 'this_or_that' | 'scale';
  depth?: number;
  options?: string[] | null;
}

interface QuestionAnswer {
  questionId: string;
  memberId: string;
  text: string;
  /** Card LdgUHoQ4R4lS — this answer landed after the reveal fired. */
  late?: boolean;
}

interface RevealPayload {
  question: QuestionDay;
  answers: QuestionAnswer[];
}

/**
 * Daily question panel — ported from `questions/ui.ts` and given a screen
 * pass (design review): the question is the hero in a serif card, both
 * partners' answered/unanswered state is explicit, and the submit stays
 * disabled until there is real text.
 */
export function QuestionsPanel({ members, intent }: { members: { memberId: string; displayName: string }[]; intent?: { kind: 'question' } | null }) {
  const me = session();

  const [question, setQuestion] = useState<QuestionDay | null>(null);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [history, setHistory] = useState<RevealPayload[] | null>(null);
  const [answered, setAnswered] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  /* Card 7qBMJvciMyvQ used to offer "save my answer to the Notebook". Removed
     per founder email 2026-10-19: the toggle read as redundant next to the
     answer composer, so the answer no longer files to the Notebook. */
  /* The full dated history (card IlGn5h3p3wDk) opens IN this panel now that
     its own More-menu screen is gone — the same screen, one door. */
  const [fullHistory, setFullHistory] = useState(false);
  /* Engine actions (PRD v2 §6): skip replaces today's question while nobody
     has answered; "not for us" also opts the theme out for good. Once the
     day cannot be swapped, the card says so instead of offering a dead tap. */
  const [tomorrow, setTomorrow] = useState(false);
  /* Structured formats (PRD v2 §8): this_or_that and scale collect a CHOICE,
     and the answer text is that choice — the note is optional and joined with
     a blank line, so reveal and history need no new shape. */
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');

  /* ── Desktop uplift A2 (card BGomsGG5sYBq) — the RevealHistory rail ──────
     At the SHIPPED notebook three-zone token (>=1100px, matchMedia, same
     seam Thread.tsx uses for its rail) this screen becomes two panes: the
     reveal stream keeps the left track and the RevealHistory screen — today
     a detour that replaces the panel — renders as the right rail instead.
     Below 1100px `railLive` is false, so the aside never enters the DOM and
     the "See every reveal" door keeps its shipped full-screen path. */
  const [railLive, setRailLive] = useState(false);
  const [railDismissed, setRailDismissed] = useState(false);
  const railRef = useRef<HTMLElement | null>(null);
  /** Set only by the door, so opening on load never steals the caret. */
  const railWantsFocus = useRef(false);
  const showRail = railLive && !railDismissed;
  /** Open: explicit (the door only), so first paint never steals focus.
   *  Close: same contract as Thread's rail — Esc or the rail's Close, focus
   *  returns to the door that opened it (#btn-us-full-history). */
  const openRail = () => {
    railWantsFocus.current = true;
    setRailDismissed(false);
  };
  const closeRail = () => {
    setRailDismissed(true);
    document.getElementById('btn-us-full-history')?.focus();
  };

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1100px)');
    const sync = () => setRailLive(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  /* Esc closes the rail with the same contract Overlay/Thread use, and focus
     returns to the door that opened it. */
  useEffect(() => {
    if (!showRail) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeRail();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [showRail]);

  /* Opening the rail moves focus to its Close control (the rail's first
     focusable), but only for an explicit open — never on first paint. */
  useEffect(() => {
    if (!showRail || !railWantsFocus.current) return;
    railWantsFocus.current = false;
    railRef.current?.querySelector<HTMLElement>('.rh-close, button, a')?.focus();
  }, [showRail]);

  /* Resizing up to the desktop token retires the full-screen detour, which
     has no place beside the rail it just became. */
  useEffect(() => {
    if (railLive && fullHistory) setFullHistory(false);
  }, [railLive, fullHistory]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await api<{ reveals: RevealPayload[] }>('/api/questions/history');
      setHistory(data.reveals ?? []);
    } catch {
      setHistory([]);
    }
  }, []);

  // Compose menu deep-link (design v1.1 §5.2): Question routes here AND scrolls
  // the today-question's answer field into view once it has loaded.
  const intentRef = useRef(intent);
  intentRef.current = intent;
  useEffect(() => {
    if (intentRef.current?.kind !== 'question') return;
    const t = window.setTimeout(() => {
      document.getElementById('q-answer')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (document.getElementById('q-answer') as HTMLTextAreaElement | null)?.focus();
    }, 400);
    return () => window.clearTimeout(t);
  }, [question]);

  const load = useCallback(async () => {
    // Card iKkKUEi2QZHZ — "question content occasionally errors". The panel
    // used to fail hard on the FIRST rejected request, so one transient error
    // (room waking, blip on the wire) showed "Failed to load" until reload.
    // One retry, then report; the worker logs the real cause either way.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const data = await api<{ question: QuestionDay }>('/api/questions/today');
        const qd = data.question;
        if (!qd || typeof qd.prompt !== 'string') {
          // A well-formed 200 with no usable question is still a failure —
          // don't render an empty hero and call it fine.
          throw new Error('malformed question payload');
        }
        setQuestion(qd);

        if (qd.state === 'revealed') {
          try {
            const payload = await api<RevealPayload>(`/api/questions/${qd.questionId}`);
            setReveal(payload);
          } catch {
            setReveal(null);
          }
        }
        void loadHistory();
        return;
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        console.error('[Pear] daily question load failed', detail, `attempt=${attempt + 1}`);
        if (attempt === 0) {
          await new Promise(r => setTimeout(r, 800));
          continue;
        }
        setFailed(true);
      }
    }
  }, [loadHistory]);

  // session() returns a NEW object every render, so using `me` as a
  // dependency re-ran this effect on every render — an endless load() loop
  // (card iKkKUEi2QZHZ: the visible symptom was "question content
  // occasionally errors"). Key on the id instead: it only changes on a real
  // sign-in, so the panel loads once per session.
  const meKey = me ? `${me.coupleId}:${me.memberId}` : '';
  useEffect(() => {
    if (meKey) void load();
  }, [meKey, load]);

  // First-move copy for this screen now lives in the ⓘ tooltip beside the
  // "Today's question" heading (card tFMiqgWdBdqW), so there is no visible
  // one-liner to retire and no `markHintSeen('us')` to fire here. The other
  // three tabs keep their .tab-hint paragraphs and their dismissal.

  if (!me) return <p className="q-guard">Please log in first.</p>;
  if (failed) return <p className="q-guard">Failed to load daily question.</p>;
  if (!question) {
    return (
      <div className={`us-q-grid${showRail ? ' is-split' : ''}`}>
        <div className="q-panel" aria-busy="true">
          <SkeletonBlock lines={4} />
        </div>
      </div>
    );
  }

  const sendAnswer = async (text: string) => {
    if (sending || answered || !text) return;

    setSending(true);
    try {
      const result = await api<{ question: QuestionDay; reveal: RevealPayload | null }>(
        `/api/questions/${question.questionId}/answer`,
        { method: 'POST', body: { text } },
      );
      // The status row reads answeredBy off the QUESTION, not off local state,
      // so a successful answer must mark the question too — otherwise the chip
      // keeps saying "You haven't answered yet" right after you answered.
      setQuestion((prev) => prev && me?.memberId && !prev.answeredBy.includes(me.memberId)
        ? { ...prev, answeredBy: [...prev.answeredBy, me.memberId] }
        : prev);
      if (result.reveal) {
        setReveal(result.reveal);
        setDraft('');
        setNote('');
        void loadHistory();
      } else {
        setAnswered(true);
        setDraft('');
        setNote('');
      }
      // Interaction clock (card hSgJtaF6deWm): the answer used to complete
      // on the Thread card, which emitted this beacon. The answer pill moved
      // the core action HERE (founder email 2026-10-21), so the panel emits
      // it now — without this the `question/answer` metric is silently lost
      // for every answering path except a card nobody uses any more. seq -1
      // means "unknown stream position"; todayMetrics falls back to the
      // question card's own stamp, so ms_to_act is still measured from first
      // paint. Never throws (todayMetrics is best-effort by contract).
      logTodayAction('question', -1, 'answer');
      // Solo accumulation (card Pn1wrppZMWEr): the sealed stack on the solo
      // home re-reads its count from this, so the number is never stale.
      window.dispatchEvent(new CustomEvent('pear:questions-changed'));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Submission Failed');
    } finally {
      setSending(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void sendAnswer(draft.trim());
  };

  /** A structured answer: the choice is the answer and the note rides under
   *  it — OR the typed words alone are the answer (card X0RAk2pTLM3s,
   *  Jonathan 2026-11-05: "free text entry can be the user's sole choice").
   *  A typed answer with nothing picked is complete, not half an answer, so
   *  it is sent as the answer text on its own; the reveal and the history
   *  read `text` either way, so neither needs a new shape. */
  const submitChoice = (e: FormEvent) => {
    e.preventDefault();
    const typed = note.trim();
    if (!choice && !typed) return;
    void sendAnswer(choice ? (typed ? `${choice}\n\n${typed}` : choice) : typed);
  };

  const canSubmit = draft.trim().length > 0 && !sending && !answered;
  const canSubmitChoice = (choice.length > 0 || note.trim().length > 0) && !sending && !answered;
  const format = question?.format ?? 'open';

  const askForAnother = async (kind: 'skip' | 'not-for-us') => {
    if (!question || sending) return;
    setSending(true);
    try {
      const result = await api<{ swapped: boolean; question: QuestionDay | null }>(
        `/api/questions/${question.questionId}/${kind}`,
        { method: 'POST' },
      );
      if (result.swapped && result.question) {
        setQuestion(result.question);
        setReveal(null);
        setDraft('');
        setChoice('');
        setNote('');
        setTomorrow(false);
        window.dispatchEvent(new CustomEvent('pear:questions-changed'));
      } else {
        setTomorrow(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('already_answered')) setTomorrow(true);
      else alert(msg || 'That did not work');
    } finally {
      setSending(false);
    }
  };

  /** Reveal history minus the reveal rendered as the hero above it — today's
   *  question showed up twice on this screen, which read as a duplicated
   *  question (card JZvjYBvov0uR, reported 2026-10-07). The dedicated Reveal
   *  history screen keeps today's entry; only this inline list skips it.
   *  Newest first, sorted by the couple's own day (2026-10-17 email). */
  const pastReveals = (history?.filter(rp => rp.question.questionId !== question.questionId) ?? [])
    .slice()
    .sort((a, b) => (b.question.dateKey < a.question.dateKey ? -1 : 1));

  /* The full history replaces the day view while it is open, and its Close
     hands the panel straight back — no navigation, no second screen. */
  if (fullHistory) {
    return <RevealHistory members={members} onClose={() => setFullHistory(false)} />;
  }

  return (
    <div className={`us-q-grid${showRail ? ' is-split' : ''}`}>
    <div className="q-panel">
      {/* A reveal with no answers in it is not a reveal (2026-10-17 email):
          an empty "Revealed together" box read as the question ghosting on
          screen. Zero answers falls back to the ordinary unanswered state —
          prompt, who-hasn't-answered, composer — so it stays answerable and
          the empty box disappears gracefully. */}
      {reveal && reveal.answers.length > 0 ? (
        <>
          {/* No confetti here (Jonathan 2026-10-30, second call, card
              ZXJan7hSZq2G: "no confetti just motion"). The reveal keeps its
              own fade-and-rise — .reveal-container / revealUp below — and that
              motion IS the moment. Particles are reserved for a shared game
              win (card psHEfEkK9cT2), so a one-sided reveal and a
              both-answered reveal differ only in their copy. */}
          <div className="pear-q-eyebrow">
            {/* One-sided reveal says so (2026-10-17 email): "Revealed together"
                only earns its keep when both answers are in. */}
            {reveal.answers.length > 1 ? 'Revealed together' : 'Revealed — one answer in'}
          </div>
          <div className="pear-q-prompt pear-q-hero">{reveal.question.prompt}</div>
          <div className="pear-q-answers-row pear-q-section">
            {reveal.answers.map(ans => {
              const isMine = me.memberId === ans.memberId;
              const pname = members.find(m => m.memberId === ans.memberId)?.displayName ?? 'Your partner';
              return (
                <div className="pear-q-answer-card pear-q-answer" key={ans.memberId}>
                  <span className="pear-q-role-label">{isMine ? 'You said' : `${pname} said`}</span>
                  {/* Card LdgUHoQ4R4lS: an answer that arrived after the
                      reveal says so, rather than passing as part of it. */}
                  {ans.late && <span className="pear-q-chip is-late">Answered late</span>}
                  <div className={`pear-q-answer-text ${isMine ? 'is-mine' : 'is-theirs'}`}>{ans.text}</div>
                </div>
              );
            })}
          </div>

          {/* Card LdgUHoQ4R4lS: non-response is recoverable. The partner who
              never answered still gets the composer here — their answer saves,
              joins the reveal, and is marked late for both of them. */}
          {!reveal.answers.some(a => a.memberId === me.memberId) && (
            <Composer
              id="q-answer"
              formClassName="pear-q-form pear-q-late-form"
              label="Your answer — it joins the reveal as late"
              placeholder="You haven't answered this one yet…"
              value={draft}
              onChange={setDraft}
              onSubmit={submit}
              disabled={sending}
              sendClassName="pear-q-btn"
              sendDisabled={!canSubmit}
              sendLabel={sending ? 'Sending…' : 'Add my answer'}
              controlsClassName="pear-q-controls"
            />
          )}
        </>
      ) : (
        <>
          {/* No eyebrow here: the Us screen's section heading already reads
              "Today's question" (card JD6Cujrq3SQw — the label was rendering
              twice, stacked). The card carries the prompt alone; the revealed
              branch keeps its own "Revealed together" label, which says
              something the heading does not. */}
          <div className="pear-q-prompt pear-q-hero">{question.prompt}</div>

          {/* State before action: who has/hasn't answered, then the composer. */}
          <div className="pear-q-status-row" role="status">
            {me.memberId && question.answeredBy.includes(me.memberId) ? (
              <span className="pear-q-chip is-answered">You answered</span>
            ) : (
              <span className="pear-q-chip">You haven't answered yet</span>
            )}
            {(() => {
              const partner = members.find(m => m.memberId !== me.memberId);
              if (!partner) return <span className="pear-q-chip">Waiting on your partner to join</span>;
              const joined = question.answeredBy.includes(partner.memberId);
              return joined ? (
                <span className="pear-q-chip is-answered">{partner.displayName} answered</span>
              ) : (
                <span className="pear-q-chip">{partner.displayName} hasn't answered yet</span>
              );
            })()}
          </div>

          {/* Format gate (card a5fbGvPIggaR): `scale` is deprecated, so the
              only structured draw left is this_or_that and EVERYTHING else —
              open, a scale row an old store still carries, an unknown format —
              collects free text. The 1-10 scale prompt is gone with it: the
              choice a scale question once asked is now written in the
              composer like any other answer. */}
          {format !== 'this_or_that' ? (
            <Composer
              id="q-answer"
              formClassName="pear-q-form"
              label="Your answer"
              placeholder="Type your private answer…"
              value={draft}
              onChange={setDraft}
              onSubmit={submit}
              disabled={answered}
              sendClassName="pear-q-btn"
              sendDisabled={!canSubmit}
              sendLabel={
                answered
                  ? 'Locked in — opens when they answer, or after a day'
                  : sending ? 'Sending…' : 'Submit answer'
              }
              controlsClassName="pear-q-controls"
            />
          ) : (
            <form className="pear-q-form pear-q-form-choice" onSubmit={submitChoice}>
              <div className="pear-q-choice-label">
                Pick one, or write your own
              </div>

              <div className="pear-q-choices" role="group" aria-label="Pick one, or write your own">
                {(question.options ?? []).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`pear-q-choice${choice === opt ? ' is-selected' : ''}`}
                    aria-pressed={choice === opt}
                    data-choice={opt}
                    onClick={() => setChoice(opt)}
                    disabled={answered || sending}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              <textarea
                id="q-note"
                className="pear-q-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note — or write your own answer"
                maxLength={400}
                disabled={answered || sending}
              />

              <div className="pear-q-controls">
                <button
                  type="submit"
                  className="pear-q-btn"
                  id="btn-q-submit-choice"
                  disabled={!canSubmitChoice}
                >
                  {answered
                    ? 'Locked in — opens when they answer, or after a day'
                    : sending ? 'Sending…' : 'Submit answer'}
                </button>
              </div>
            </form>
          )}

          {/* Two quiet actions, never competing with Submit (PRD v2 §6).
              "Not for us" is the stronger one: it removes the theme for good,
              so its label says the consequence rather than hiding it. */}
          <div className="pear-q-actions">
            {tomorrow ? (
              <span className="pear-q-tomorrow" id="q-tomorrow">
                New question tomorrow.
              </span>
            ) : (
              <>
                <button
                  type="button"
                  id="btn-q-skip"
                  className="pear-q-act"
                  onClick={() => void askForAnother('skip')}
                  disabled={sending}
                >
                  Skip this one
                </button>
                <span className="pear-q-act-sep" aria-hidden="true">·</span>
                <button
                  type="button"
                  id="btn-q-not-for-us"
                  className="pear-q-act"
                  title="We won't ask about this theme again"
                  onClick={() => void askForAnother('not-for-us')}
                  disabled={sending}
                >
                  Not for us
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* The section's HEADING and its one door always render (card
          IlGn5h3p3wDk) — the full dated history has to be reachable from Us
          > Questions even when today's reveal is the only one. Only the
          inline LIST hides in that case: a "No reveals yet." line under a
          just-revealed hero contradicts itself (card JZvjYBvov0uR). */}
      <div className="pear-q-hist-section">
        <div className="pear-q-hist-head">
          <h3>Reveal history</h3>
          {/* The order is part of the copy, not just the layout (2026-10-17 email). */}
          <span className="pear-q-hist-order">Newest first</span>
          <button
            type="button"
            className="pear-q-hist-open"
            id="btn-us-full-history"
            aria-expanded={showRail}
            onClick={() => (railLive ? openRail() : setFullHistory(true))}
          >
            See every reveal
          </button>
        </div>
        {(history === null || history.length === 0 || pastReveals.length > 0) && (
          <ul className="pear-q-hist-list">
            {history === null && <SkeletonBlock lines={2} />}
            {history !== null && pastReveals.length === 0 && <li className="pear-q-hist-empty">No reveals yet.</li>}
            {/* Founder email 2026-10-27: the inline list must use the SAME
                implementation as the full reveal history — one bounded card per
                question, category chip, date, side-by-side answers with the
                partner labels pinned at the top, measured Read more. It used to
                render its own stacked variant, so the same pair of answers read
                differently depending on where you looked. */}
            {pastReveals.map(rp => (
              <li className="pear-q-hist-card is-bounded" key={rp.question.questionId}>
                <RevealCard
                  reveal={rp}
                  order={members.map(m => m.memberId)}
                  nameFor={memberId => members.find(m => m.memberId === memberId)?.displayName ?? 'Your partner'}
                  emptyText="No answers arrived before this reveal opened."
                  testId="questions-inline"
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
    {/* ---- Desktop right rail (card BGomsGG5sYBq, A2) ----
        The RevealHistory SCREEN becomes the rail: same component, same
        fetch, same empty/error states — only its container changes, and it
        enters the DOM only at the >=1100px three-zone token, so below 760px
        (and at every phone width) nothing here exists. The rail reuses the
        notebook .nb-context primitive, so its breakpoint, surface, radius and
        sticky behaviour are the shipped ones. */}
    {showRail && (
      <aside
        className="nb-context pear-q-rail"
        data-q-rail
        aria-label="Reveal history"
        ref={railRef}
      >
        <RevealHistory members={members} onClose={closeRail} />
      </aside>
    )}
    </div>
  );
}